import { config } from 'config/config';
import * as CryptoTS from 'crypto-ts';
import getConfig from 'next/config';
import { NextRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { PlatformCountryEnum, PlatformEnum } from 'src/shared/enums';
import { IBaseUserData, IUser } from 'src/shared/models';
import { format } from 'url';
import { genericPhoneRegex, phoneRegex } from './user-validations';

const { EMAIL_ENCODER_KEY, PLATFORM_ID } = getConfig().publicRuntimeConfig;

const { PLATFORM, PLATFORM_TYPE } = config.APP;

export const isConPlatform: boolean = PLATFORM_TYPE === 'con';

export const isConPlatformES: boolean = PLATFORM_ID === 'con-es';
export const isConPlatformPE: boolean =
  PLATFORM_ID === PlatformEnum.CON_PERU;
export const isConPlatformCL: boolean =
  PLATFORM_ID === PlatformEnum.CON_CHILE;
export const isConPlatformAR: boolean =
  PLATFORM_ID === PlatformEnum.CON_ARGENTINA;
export const isConPlatformCO: boolean =
  PLATFORM_ID === PlatformEnum.CON_COLOMBIA;
export const isConPlatformFR: boolean = PLATFORM_ID === PlatformEnum.CON_FRANCE;

export const getCurrencyFormat = (): string => {
  switch (PLATFORM) {
    case PlatformEnum.CON_ARGENTINA:
      return 'ARS';
    case PlatformEnum.CON_BRAZIL:
      return 'BRL';
    case PlatformEnum.CON_CHILE:
      return 'CLP';
    case PlatformEnum.CON_COLOMBIA:
      return 'COP';
    case PlatformEnum.CON_DEUTSCHLAND:
    case PlatformEnum.CON_FRANCE:
    case PlatformEnum.CON_ITALY:
    case PlatformEnum.CON_PORTUGAL:
    case PlatformEnum.CON_SPAIN:
    default:
      return 'EUR';
    case PlatformEnum.CON_PERU:
      return 'PEN';
    case PlatformEnum.CON_MEXICO:
      return 'MXN';
  }
};

export const getShortDateFormat = (): string => {
  switch (PLATFORM) {
    case PlatformEnum.CON_ARGENTINA:
    case PlatformEnum.CON_BRAZIL:
    case PlatformEnum.CON_CHILE:
    case PlatformEnum.CON_COLOMBIA:
    case PlatformEnum.CON_DEUTSCHLAND:
    case PlatformEnum.CON_FRANCE:
    case PlatformEnum.CON_ITALY:
    case PlatformEnum.CON_MEXICO:
    case PlatformEnum.CON_PERU:
    case PlatformEnum.CON_PORTUGAL:
    case PlatformEnum.CON_SPAIN:
    default:
      return 'DD/MM';
  }
};

export const hideUserAddress = (): boolean => {
  return false;
};

export const defaultLanguage = getConfig().publicRuntimeConfig.DEFAULT_LANGUAGE;

export const scrollDown = (value: string) => {
  document
    .getElementById(value)
    .scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const handleShoppingCartDirection = (): boolean => {
  return true;
};

export const getUserData = (user: IUser): IBaseUserData => {
  return {
    name: user?.salesforce?.userFirstname || user?.temporalData?.name,
    surname: user?.salesforce?.userSurname || user?.temporalData?.lastName,
    email: user?.salesforce?.userMail || user?.email,
    birthday: user?.salesforce?.userBirthdate || user?.temporalData?.birthDate,
  };
};

export const setDetailUrl = (
  router: NextRouter,
  basePath: string,
  detailPath: string,
) => {
  const { asPath, pathname, query, replace } = router;
  const { publicRuntimeConfig } = getConfig();
  const { PLATFORM_PREFIX } = publicRuntimeConfig;

  function constructDetailUrl(basePath: string, query: ParsedUrlQuery): string {
    const { id, ...restParams } = query;
    const basePage = `${basePath}/${id}`;

    const paramsArr = Object.keys(restParams);

    if (paramsArr.length === 0) {
      return basePage;
    }

    const stringifiedParams = paramsArr.reduce((acc, cur) => {
      const operator = acc ? '&' : '?';
      const currentParam = `${[cur]}=${restParams[cur]}`;
      return acc + operator + currentParam;
    }, '');

    return `${basePage}${stringifiedParams}`;
  }

  if (asPath.includes(detailPath)) {
    const page = constructDetailUrl(basePath, query);
    const newPath = `${PLATFORM_PREFIX || ''}${format(page)}`;
    const replacedPath = asPath.replace(PLATFORM_PREFIX, '');

    replace(replacedPath, newPath, { shallow: true });
  }
};

// Encrypt utils
export const encryptValue = (value: string): string => {
  if (!value) return '';
  return CryptoTS.AES.encrypt(value, EMAIL_ENCODER_KEY).toString();
};

export const decryptValue = (encryptedValue: string) => {
  if (!encryptValue) return '';
  return CryptoTS.AES.decrypt(encryptedValue, EMAIL_ENCODER_KEY).toString(
    CryptoTS.enc.Utf8,
  );
};

// URL utils
export const getSegments = () => {
  return window.location.href
    .replace(window.location.origin + '/', '')
    .split('/');
};

export const joinSegments = (segments: any[], separator = '/') => {
  const parsedSegments = [];

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment) continue;

    if (typeof segment === 'string') {
      parsedSegments.push(segment);
    } else if (typeof segment === 'object') {
      if (segment.toString === Object.prototype.toString) {
        for (const key in segment) {
          if ({}.hasOwnProperty.call(segment, key) && segment[key]) {
            parsedSegments.push(key);
          }
        }
      } else {
        parsedSegments.push(segment.toString());
      }
    } else continue;
  }

  return parsedSegments.join(separator);
};

// Format Utils
export const slugify = (value: string) => {
  return value
    ?.replace(/[.,\/#!$%\^&\*;:{}=`~()]/g, '')
    .replace(/\s+/g, '_')
    .toLowerCase();
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const phoneNumberLength = (prefix: string) => {
  const countryPrefix = PlatformCountryEnum;
  switch (prefix) {
    case countryPrefix.CON_ARGENTINA:
      return 11;
    case countryPrefix.CON_BRAZIL:
      return 11;
    case countryPrefix.CON_COLOMBIA:
    case countryPrefix.CON_MEXICO:
      return 10;
    case countryPrefix.CON_DEUTSCHLAND:
      return 13;
    case countryPrefix.CON_ITALY:
      return 15;
    case countryPrefix.CON_CHILE:
    case countryPrefix.CON_FRANCE:
    case countryPrefix.CON_PERU:
    case countryPrefix.CON_PORTUGAL:
    case countryPrefix.CON_SPAIN:
      return 9;
    default:
      return 12;
  }
};

export const phoneNumberPattern = (prefix: string) => {
  return phoneRegex[prefix] ?? genericPhoneRegex;
}

export const formatLanguageForSalesforce = (language: string) => {
  let formattedLanguage = 'en-US';

  switch (language) {
    case 'de-DE':
    case 'es-ES':
    case 'fr-FR':
    case 'it-IT':
    case 'pt-PT':
      formattedLanguage = language.slice(0, 2);
      break;
    case 'es-AR':
    case 'es-CL':
    case 'es-CO':
    case 'es-MX':
    case 'es-PE':
    case 'pt-BR':
    default:
      formattedLanguage = language.slice(0, 2).concat('_');
      formattedLanguage = formattedLanguage.concat(language.slice(3, 5));
      break;
  }

  return formattedLanguage;
};

export const getCustomerCareEmail = (): string => {
  switch (PLATFORM) {
    case PlatformEnum.CON_ARGENTINA:
      return 'communitycare.ar@isdin.com';
    case PlatformEnum.CON_BRAZIL:
      return 'communitycare.br@isdin.com';
    case PlatformEnum.CON_CHILE:
      return 'communitycare.cl@isdin.com';
    case PlatformEnum.CON_COLOMBIA:
      return 'communitycare.co@isdin.com';
    case PlatformEnum.CON_DEUTSCHLAND:
      return 'communitycare.de@isdin.com';
    case PlatformEnum.CON_FRANCE:
      return 'communitycare.fr@isdin.com';
    case PlatformEnum.CON_ITALY:
      return 'communitycare.it@isdin.com';
    case PlatformEnum.CON_MEXICO:
      return 'communitycare.mx@isdin.com';
    case PlatformEnum.CON_PERU:
      return 'communitycare.pe@isdin.com';
    case PlatformEnum.CON_PORTUGAL:
      return 'communitycare.pt@isdin.com';
    case PlatformEnum.CON_SPAIN:
    default:
      return 'communitycare.es@isdin.com';
  }
};
